import { z } from 'zod';
import type { FieldErrors } from 'react-hook-form';
import type { TFunction } from 'i18next';
import type { OperationRelationshipDto } from '../../api/models/OperationRelationshipDto';
import type { ResourceConstraintsDto } from '../../api/models/ResourceConstraintsDto';
import type { ResourceRequirementDto } from '../../api/models/ResourceRequirementDto';
import { isPlainObject } from '../../utils/localizeFormErrors';

type Translate = TFunction;

/**
 * Form schema of the routing dialog, adapted to the generated DTO types.
 *
 * The nested arrays hold the operations, relationships and requirements exactly as the backend
 * serializes them (see RoutingMapper). The backend writes null for every unset field and the
 * dialog hands these objects to the resolver unchanged, so every optional field below also has
 * to accept null. Before this, a NULL description on a single operation (typical for data that
 * came from a customer import rather than from this dialog) failed the validation and the save
 * silently did nothing.
 */
export const createRoutingSchema = (t: Translate) => z.object({
  name: z.string().min(1, t('validation.required')),
  description: z.string().nullable().optional(),
  materialId: z.string().min(1, t('validation.required')),
  routingType: z.enum(['PRODUCT_ROUTING', 'INTERMEDIATE_ROUTING']).optional(),
  // Mon=0..Sun=6 (bits 0-6) - interpreted only by applicability=WEEKDAYS. On INACTIVE the
  // value is kept so that reactivating brings the days back.
  weekdayMask: z.number().min(0).max(255).optional(),
  // Discriminator: how the routing is picked when a production order is created.
  applicability: z.enum(['DEFAULT', 'WEEKDAYS', 'HOLIDAY', 'INACTIVE']).optional(),
  operations: z.array(z.object({
    uuid: z.string().nullable().optional(),
    routingId: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    durationSeconds: z.number().nullable().optional(),
    inputQuantities: z.array(z.any()).nullable().optional(),
    primaryResourceTypeId: z.string().nullable().optional(),
    primaryResourceTypeName: z.string().nullable().optional(),
    minDistancePredecessor: z.number().nullable().optional(),
    maxDistancePredecessor: z.number().nullable().optional(),
    id: z.string().nullable().optional() // Added for CrudPage compatibility
  })).optional(),
  operationRelationships: z.array(z.object({
    uuid: z.string().nullable().optional(),
    routingId: z.string().nullable().optional(),
    predecessorId: z.string().nullable().optional(),
    successorId: z.string().nullable().optional(),
    relationType: z.enum(['START_TO_START', 'START_TO_END', 'END_TO_START', 'END_TO_END']).nullable().optional(),
    relationshipCategory: z.enum(['TIME', 'CONSTRAINT']).nullable().optional(),
    minDistance: z.number().nullable().optional(),
    maxDistance: z.number().nullable().optional(),
    resourceConstraints: z.object({
      predecessorResourceIds: z.array(z.string()).nullable().optional(),
      successorResourceIds: z.array(z.string()).nullable().optional(),
      constraintType: z.enum(['CONSTRAINED_RESOURCES', 'DIFFERENT_RESOURCES', 'SAME_RESOURCE', 'RESOURCE_DEPENDENT', 'RESOURCE_EXCLUSIVE', 'RESOURCE_SHARED']).nullable().optional()
    }).nullable().optional()
  })).optional(),
  resourceRequirements: z.array(z.object({
    uuid: z.string().nullable().optional(),
    fromOperationId: z.string().nullable().optional(),
    toOperationId: z.string().nullable().optional(),
    fromRelation: z.enum(['START', 'END']).nullable().optional(),
    toRelation: z.enum(['START', 'END']).nullable().optional(),
    resources: z.array(z.any()).nullable().optional(),
    conditional: z.boolean().nullable().optional(),
    conditionalOperationId: z.string().nullable().optional(),
    conditionalResourceIds: z.array(z.string()).nullable().optional()
  })).optional()
});

export type RoutingFormData = z.infer<ReturnType<typeof createRoutingSchema>>;

/**
 * Localizes zod's default messages ("Expected string, received null") for the routing form.
 * Messages given explicitly in the schema (`.min(1, t('validation.required'))`) take precedence
 * over this map, so the required-field texts stay as they are.
 */
export const createRoutingErrorMap = (t: Translate): z.ZodErrorMap => (issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: t('validation.required', 'Pflichtfeld') };
      }
      return { message: t('routing.form.validationFailed.invalidValue', 'Ungültiger Wert') };
    case z.ZodIssueCode.invalid_enum_value:
    case z.ZodIssueCode.invalid_literal:
    case z.ZodIssueCode.invalid_union:
      return { message: t('routing.form.validationFailed.invalidValue', 'Ungültiger Wert') };
    case z.ZodIssueCode.too_small:
    case z.ZodIssueCode.too_big:
      return { message: t('routing.form.validationFailed.outOfRange', 'Wert außerhalb des gültigen Bereichs') };
    default:
      return { message: ctx.defaultError };
  }
};

type Nullable<T> = { [K in keyof T]?: T[K] | null };

/** A relationship as the form, the dialog state or the backend holds it: every field may be null. */
export type OperationRelationshipInput = Nullable<Omit<OperationRelationshipDto, 'resourceConstraints'>> & {
  resourceConstraints?: Nullable<ResourceConstraintsDto> | null;
};

/** A resource requirement as the form, the dialog state or the backend holds it. */
export type ResourceRequirementInput = Nullable<ResourceRequirementDto>;

/**
 * Builds the relationship DTOs for the save request. Entries without both operations or
 * without a category are dropped, and every null is mapped to undefined so that no null
 * reaches the API through a type cast, whichever source the dialog picked.
 */
export function toOperationRelationshipDtos(
  input: OperationRelationshipInput[] | null | undefined,
): OperationRelationshipDto[] {
  return (input ?? [])
    .filter(rel => rel.predecessorId && rel.successorId && rel.relationshipCategory)
    .map(rel => ({
      ...rel,
      uuid: rel.uuid ?? undefined,
      routingId: rel.routingId ?? undefined,
      predecessorId: rel.predecessorId!,
      successorId: rel.successorId!,
      relationshipCategory: rel.relationshipCategory!,
      relationType: rel.relationType || undefined,
      minDistance: rel.minDistance ?? undefined,
      maxDistance: rel.maxDistance ?? undefined,
      resourceConstraints: rel.resourceConstraints ? {
        constraintType: rel.resourceConstraints.constraintType || undefined,
        predecessorResourceIds: rel.resourceConstraints.predecessorResourceIds || undefined,
        successorResourceIds: rel.resourceConstraints.successorResourceIds || undefined,
      } : undefined,
    }));
}

/** Builds the resource requirement DTOs for the save request; every null becomes undefined. */
export function toResourceRequirementDtos(
  input: ResourceRequirementInput[] | null | undefined,
): ResourceRequirementDto[] {
  return (input ?? []).map(req => ({
    ...req,
    uuid: req.uuid ?? undefined,
    fromOperationId: req.fromOperationId ?? undefined,
    toOperationId: req.toOperationId ?? undefined,
    fromRelation: req.fromRelation ?? undefined,
    toRelation: req.toRelation ?? undefined,
    resourceTypeUuid: req.resourceTypeUuid ?? undefined,
    resources: req.resources ?? undefined,
    conditional: req.conditional ?? undefined,
    conditionalOperationId: req.conditionalOperationId ?? undefined,
    conditionalResourceIds: req.conditionalResourceIds ?? undefined,
  }));
}

const SECTION_LABELS: Record<string, { key: string; defaultValue: string }> = {
  operations: { key: 'routing.form.validationFailed.operations', defaultValue: 'Vorgänge' },
  operationRelationships: { key: 'routing.form.validationFailed.operationRelationships', defaultValue: 'Vorgangsbeziehungen' },
  resourceRequirements: { key: 'routing.form.validationFailed.resourceRequirements', defaultValue: 'Ressourcenanforderungen' },
};

/**
 * Flattens the react-hook-form error tree of the routing form into one line per failed field.
 *
 * The dialog renders messages only for name, material and description. An error inside one of
 * the nested arrays (operation 3, field "description") would otherwise be invisible, and the
 * resolver blocks the save without any feedback. Entries of the nested arrays are reported
 * with their 1-based position, e.g. "Vorgänge 3 / description: Ungültiger Wert".
 *
 * Array-level and group-level errors live under `root` in react-hook-form and are reported
 * under the path of their parent. A node with its own message is still descended into, so
 * failing children of a failing parent are listed as well. Only plain objects and arrays are
 * visited: the `ref` of a field error points at a DOM node or a Syncfusion instance.
 */
export function summarizeRoutingFormErrors(errors: FieldErrors, t: Translate): string[] {
  const lines: string[] = [];
  const seen = new WeakSet<object>();

  const visit = (node: unknown, path: string[]) => {
    if (!Array.isArray(node) && !isPlainObject(node)) return;
    if (seen.has(node)) return;
    seen.add(node);
    const record = node as Record<string, unknown>;
    if (typeof record.message === 'string' && record.message) {
      lines.push(`${formatPath(path, t)}: ${record.message}`);
    }
    if (record.root) {
      visit(record.root, path);
    }
    if (Array.isArray(node)) {
      node.forEach((child, index) => visit(child, [...path, String(index + 1)]));
      return;
    }
    for (const [key, child] of Object.entries(record)) {
      if (key === 'ref' || key === 'type' || key === 'types' || key === 'message' || key === 'root') continue;
      visit(child, [...path, key]);
    }
  };

  visit(errors, []);
  return lines;
}

function formatPath(path: string[], t: Translate): string {
  if (path.length === 0) {
    return t('routing.form.validationFailed.form', 'Formular');
  }
  const [section, ...rest] = path;
  const sectionLabel = SECTION_LABELS[section];
  if (!sectionLabel) {
    return path.join('.');
  }
  const label = t(sectionLabel.key, sectionLabel.defaultValue);
  if (rest.length === 0) {
    return label;
  }
  const [index, ...fields] = rest;
  return fields.length > 0 ? `${label} ${index} / ${fields.join('.')}` : `${label} ${index}`;
}
